"use client";

import { Icon } from "@/components/ui/icon";
import { useJobWizardStore } from "@/features/jobs/store/job-store";
import { stripHtmlTags } from "@/lib/string/strip/strip-html";
import { LearnMoreIconIllustration } from "@/components/illustrations/learn-more-icon-illustration";
import { JobCloneNotice } from "./clone-notice";
import { stepLabels } from "./constants";

export function RadialProgress({
  stepNum,
  isActive,
  isComplete,
  progress = 0,
}: {
  stepNum: number;
  isActive: boolean;
  isComplete: boolean;
  progress?: number;
}) {
  const size = 18;
  const strokeWidth = 2;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const clampedProgress = Math.max(0, Math.min(1, progress));
  const offset = circumference * (1 - clampedProgress);

  if (isComplete) {
    return (
      <Icon
        name="IconCircleCheck"
        fill="filled"
        size={18}
        className="text-[#44BA82]"
      />
    );
  }

  return (
    <div className="relative size-[18px] flex items-center justify-center">
      <svg width={size} height={size} className="absolute inset-0 -rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="var(--border-subtle-alpha)"
          strokeWidth={strokeWidth}
        />
        {isActive && (
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="var(--heading)"
            strokeWidth={strokeWidth}
            strokeDasharray={circumference}
            strokeDashoffset={clampedProgress > 0 ? offset : circumference}
            strokeLinecap="round"
            style={{
              transition: "stroke-dashoffset 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
            }}
          />
        )}
      </svg>
      <span className="text-[8px] font-medium text-heading text-center leading-none">
        {stepNum}
      </span>
    </div>
  );
}

export function useStepProgress(step: number) {
  const { form } = useJobWizardStore();
  const steps: Record<number, number> = {};
  const THIRD = 1 / 3;

  if (step >= 1) {
    let s1 = 0;
    if (form.title.trim()) s1 += THIRD;
    if (form.managerId) s1 += THIRD;
    if (stripHtmlTags(form.description)) s1 += THIRD;
    steps[1] = Math.min(1, s1);
  } else {
    steps[1] = 0;
  }

  if (step >= 2) {
    steps[2] = form.stages.length >= 2 ? 1 : 0.5;
  } else {
    steps[2] = 0;
  }

  if (step >= 3) {
    let s3 = 0;
    if (form.uploadedFiles.length > 0) s3 += 0.5;
    if (form.importUrls.length > 0) s3 += 0.5;
    steps[3] = Math.min(1, s3);
  } else {
    steps[3] = 0;
  }

  steps[4] = step >= 4 ? 1 : 0;

  return steps;
}

export function WizardSidebar({
  step,
  maxStepReached,
  onStepClick,
  lockFirstStep = false,
}: {
  step: number;
  maxStepReached: number;
  onStepClick?: (stepNum: number) => void;
  lockFirstStep?: boolean;
}) {
  const stepProgress = useStepProgress(step);
  const { form, mode, cloneSourceName } = useJobWizardStore();
  const labels = stepLabels(form.sourcingType);
  const isCloneMode = mode === "clone";

  return (
    <div className="flex flex-col h-full border-r border-border p-6">
      <div className="flex flex-1 flex-col justify-between">
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-2">
            <p className="text-base font-medium text-heading">Job Setup</p>
            <p className="text-sm text-contrast/50 leading-5">
              Configure your job details, define your hiring pipeline stages,
              and optionally import candidate profiles.
            </p>
          </div>

          {isCloneMode && (
            <>
              <JobCloneNotice sourceName={cloneSourceName} />
              <div className="h-px bg-contrast/10" />
            </>
          )}

          <div className="flex flex-col gap-3">
            {labels.map((label, i) => {
              const stepNum = i + 1;
              const isActive = stepNum === step && step !== 4;
              const isComplete =
                stepNum < step || (stepNum === 4 && step === 4);
              const canClick =
                step < 4 &&
                stepNum <= maxStepReached &&
                !isActive &&
                !(lockFirstStep && stepNum === 1);
              return (
                <div
                  key={label}
                  role={canClick ? "button" : undefined}
                  tabIndex={canClick ? 0 : undefined}
                  onClick={canClick ? () => onStepClick?.(stepNum) : undefined}
                  onKeyDown={
                    canClick
                      ? (e) => {
                          if (e.key === "Enter" || e.key === " ")
                            onStepClick?.(stepNum);
                        }
                      : undefined
                  }
                  className={`flex gap-1.5 h-8 items-center p-1.5 rounded-md ${
                    isActive ? "bg-contrast/8" : ""
                  } ${canClick ? "cursor-pointer hover:bg-contrast/5 transition-colors" : ""}`}
                >
                  <div className="flex items-center justify-center size-5">
                    <RadialProgress
                      stepNum={stepNum}
                      isActive={isActive}
                      isComplete={isComplete}
                      progress={stepProgress[stepNum] ?? 0}
                    />
                  </div>
                  <div className="flex flex-1 items-center px-1">
                    <span className="text-sm font-medium text-contrast/70">
                      {label}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="h-px bg-contrast/10" />
        </div>

        <div className="bg-contrast/3 border border-border rounded-lg px-3 py-2.5">
          <div className="flex gap-3 items-center w-full">
            <div className="border border-border rounded-md overflow-clip shrink-0 size-8">
              <LearnMoreIconIllustration />
            </div>
            <p className="text-[13px] font-normal text-heading tracking-[-0.26px] leading-[18px] flex-1">
              Learn everything about creating efficient jobs.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
